import { useId, useState, type KeyboardEvent } from 'react';
import {
  activeTags,
  resolveTagId,
  tagUsage,
  type AnyEvent,
  type ItemRecord,
  type Manifest,
} from '@intentionalist/shared';
import { applyTagByName, removeTagFrom } from './mutations.js';
import { suggestionList } from './suggest.js';

/**
 * An item's tags (Epic 4).
 *
 * Typing a name is the whole interaction, because the glossary is explicit that
 * tags are "created in the flow of use, not administered in advance". There is
 * deliberately no screen for managing them before they are needed.
 *
 * Reuse-or-create is decided by `applyTagByName` against the normalized name
 * (FR-4.14), so typing "camping" when "Camping" exists attaches the existing tag
 * rather than quietly making a second one.
 */
export function TagEditor({
  manifest,
  item,
  onWrite,
}: {
  manifest: Manifest;
  item: ItemRecord;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const inputId = useId();
  const listId = useId();
  const headingId = useId();

  const applied = [...item.tags.entries()]
    .filter(([, decision]) => decision.present)
    .map(([tagId]) => resolveTagId(manifest, tagId))
    // A merge can leave two applied ids resolving to one surviving tag.
    .filter((tagId, index, all) => all.indexOf(tagId) === index)
    .map((tagId) => ({ tagId, tag: manifest.tags.get(tagId) }))
    .filter((entry) => entry.tag?.lifecycle?.present !== false)
    .sort((a, b) => (a.tag?.displayName?.v ?? '').localeCompare(b.tag?.displayName?.v ?? ''));

  const appliedIds = new Set(applied.map((entry) => entry.tagId));
  // Suggestions exclude what is already on the item, so the list is about what
  // could be added rather than what is there.
  //
  // Ranked by how often each is used, which FR-4.13 asks for and alphabetical
  // order silently fails: the tags someone reaches for most are the ones worth
  // offering first, and a long list sorted by name buries them.
  const usage = tagUsage(manifest);
  const suggestions = activeTags(manifest)
    .filter((tag) => !appliedIds.has(tag.tagId))
    .sort(
      (a, b) =>
        (usage.get(b.tagId) ?? 0) - (usage.get(a.tagId) ?? 0) ||
        (a.displayName?.v ?? '').localeCompare(b.displayName?.v ?? ''),
    );

  const add = () => {
    const name = draft.trim();
    if (name === '') return;
    onWrite(applyTagByName(manifest, item.itemId, name).events);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter adds the tag rather than submitting the surrounding form, which
    // would save the item mid-thought.
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      add();
    }
  };

  return (
    <section aria-labelledby={headingId} className="field">
      <h3 id={headingId}>Tags</h3>
      <label htmlFor={inputId}>Add a tag</label>

      {applied.length > 0 && (
        <ul className="tag-list" aria-label="Tags on this item">
          {applied.map(({ tagId, tag }) => (
            <li key={tagId}>
              <span>{tag?.displayName?.v ?? '(unnamed)'}</span>
              <button
                type="button"
                aria-label={`Remove tag ${tag?.displayName?.v ?? ''}`}
                onClick={() => onWrite(removeTagFrom(item.itemId, tagId))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="tag-add">
        <input
          id={inputId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type a tag and press Enter"
          autoComplete="off"
          {...suggestionList(listId, draft, suggestions)}
        />
        {/* A datalist rather than a custom menu: it is keyboard-navigable and
            screen-reader-legible without any of it being reimplemented. The
            list is rendered always and pointed at once there is a prefix to
            filter it by -- a tag vocabulary grows without limit, and all of it
            at once would cover the keyboard. */}
        <datalist id={listId}>
          {suggestions.map((tag) => (
            <option key={tag.tagId} value={tag.displayName?.v} />
          ))}
        </datalist>
        {/* Named for what it adds. The capture form now carries a colour
            editor with its own Add beside this one, and two buttons reading
            "Add" are two buttons reading "Add" to anyone not looking at them. */}
        <button type="button" aria-label="Add tag" onClick={add} disabled={draft.trim() === ''}>
          Add
        </button>
      </div>
    </section>
  );
}
